import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { robojacketsTheme } from '../../theme/robojacketsTheme';
import { groupService } from '../../services/groupService';
import { groupTypeService } from '../../services/groupTypeService';
import { userService } from '../../services/userService';
import { hasAnyRole } from '../../utils/auth';
import GroupForm from './GroupForm';
import GroupTypeForm from './GroupTypeForm';
import TeamInviteStudents from '../TeamInviteStudents';
import TeamInviteSupervisors from '../TeamInviteSupervisors';

// TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`groups-tabpanel-${index}`}
      aria-labelledby={`groups-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Groups = ({ currentUser }) => {
  const [tabValue, setTabValue] = useState(0);
  const [groups, setGroups] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Group form states
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormMode, setGroupFormMode] = useState('create');
  
  // Group type form states
  const [groupTypeFormOpen, setGroupTypeFormOpen] = useState(false);
  const [editingGroupType, setEditingGroupType] = useState(null);
  const [groupTypeFormMode, setGroupTypeFormMode] = useState('create');
  
  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'group' or 'groupType'
  
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroupType, setFilterGroupType] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsData, groupTypesData, usersData] = await Promise.all([
        groupService.getAllGroups(),
        groupTypeService.getAllGroupTypes(),
        userService.getAllUsers()
      ]);
      
      setGroups(groupsData);
      setGroupTypes(groupTypesData);
      setUsers(usersData);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter groups based on search term and group type
  const filteredGroups = groups.filter(group => {
    const matchesSearch = !searchTerm || 
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !filterGroupType || 
      getGroupTypeName(group.groupTypeId) === filterGroupType;
    
    return matchesSearch && matchesType;
  });

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupFormMode('create');
    setGroupFormOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupFormMode('edit');
    setGroupFormOpen(true);
  };

  const handleSaveGroup = async (groupData) => {
    try {
      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      if (groupFormMode === 'create') {
        await groupService.createGroup(groupData, currentUser.id);
        setSuccess('Group created successfully');
      } else {
        await groupService.updateGroup(editingGroup.id, groupData, currentUser.id);
        setSuccess('Group updated successfully');
      }
      setGroupFormOpen(false);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError('Failed to save group: ' + errorMessage);
    }
  };

  const handleCreateGroupType = () => {
    setEditingGroupType(null);
    setGroupTypeFormMode('create');
    setGroupTypeFormOpen(true);
  };

  const handleEditGroupType = (groupType) => {
    setEditingGroupType(groupType);
    setGroupTypeFormMode('edit');
    setGroupTypeFormOpen(true);
  };

  const handleSaveGroupType = async (groupTypeData) => {
    try {
      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      if (groupTypeFormMode === 'create') {
        await groupTypeService.createGroupType(groupTypeData, currentUser.id);
        setSuccess('Group type created successfully');
      } else {
        await groupTypeService.updateGroupType(editingGroupType.id, groupTypeData, currentUser.id);
        setSuccess('Group type updated successfully');
      }
      setGroupTypeFormOpen(false);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError('Failed to save group type: ' + errorMessage);
    }
  };

  const handleDeleteClick = (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      if (deleteType === 'group') {
        await groupService.deleteGroup(deleteItem.id, currentUser.id);
        setSuccess('Group deleted successfully');
      } else {
        await groupTypeService.deleteGroupType(deleteItem.id, currentUser.id);
        setSuccess('Group type deleted successfully');
      }
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError('Failed to delete: ' + errorMessage);
    }
  };

  const getUserDisplayName = (user) => {
    const preferredName = user.preferredName || user.firstName;
    const lastName = user.lastName || '';
    const graduationYear = user.graduationYear || '';
    return `${preferredName} ${lastName} ${graduationYear}`.trim();
  };

  const getGroupTypeName = (groupTypeId) => {
    const groupType = groupTypes.find(gt => gt.id === groupTypeId);
    return groupType ? groupType.name : 'Unknown';
  };

  const getPrimaryUserName = (primaryUserId) => {
    if (!primaryUserId) return 'None';
    const user = users.find(u => u.id === primaryUserId);
    return user ? getUserDisplayName(user) : 'Unknown';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: robojacketsTheme.palette.primary.main }}>
        Groups Management
      </Typography>

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Groups"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, code, or description..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Filter by Group Type</InputLabel>
                <Select
                  value={filterGroupType}
                  onChange={(e) => setFilterGroupType(e.target.value)}
                  label="Filter by Group Type"
                >
                  <MenuItem value="">All Group Types</MenuItem>
                  {groupTypes.map((groupType) => (
                    <MenuItem key={groupType.id} value={groupType.name}>
                      {groupType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Groups" />
            <Tab label="Group Types" />
            {hasAnyRole(['team_supervisor', 'coach', 'staff']) && (
              <Tab label="Invite Students" />
            )}
            {hasAnyRole(['team_supervisor', 'coach', 'staff']) && (
              <Tab label="Invite Supervisors" />
            )}
          </Tabs>

          {/* Groups Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">All Groups</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateGroup}
                sx={{
                  backgroundColor: robojacketsTheme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: robojacketsTheme.palette.primary.dark
                  }
                }}
              >
                Create Group
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Primary User</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Seeking Members</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={group.code || 'N/A'} 
                          color="primary" 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{group.description}</TableCell>
                      <TableCell>{getGroupTypeName(group.groupTypeId)}</TableCell>
                      <TableCell>{getPrimaryUserName(group.primaryUserId)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {group.userIds.map((userId) => {
                            const user = users.find(u => u.id === userId);
                            return user ? (
                              <Chip 
                                key={userId} 
                                label={getUserDisplayName(user)} 
                                size="small" 
                                variant="outlined"
                              />
                            ) : null;
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={group.seekingMembers ? 'Yes' : 'No'} 
                          color={group.seekingMembers ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={group.isActive ? 'Active' : 'Inactive'} 
                          color={group.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditGroup(group)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(group, 'group')} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredGroups.length === 0 && !loading && (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No groups found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || filterGroupType ? 'Try adjusting your search criteria.' : 'Create your first group to get started.'}
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Group Types Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Group Types</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateGroupType}
                sx={{
                  backgroundColor: robojacketsTheme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: robojacketsTheme.palette.primary.dark
                  }
                }}
              >
                Create Group Type
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupTypes.map((groupType) => (
                    <TableRow key={groupType.id}>
                      <TableCell>{groupType.name}</TableCell>
                      <TableCell>{groupType.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={groupType.isActive ? 'Active' : 'Inactive'} 
                          color={groupType.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditGroupType(groupType)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(groupType, 'groupType')} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Invite Students Tab */}
          <TabPanel value={tabValue} index={2}>
            <TeamInviteStudents />
          </TabPanel>

          {/* Invite Supervisors Tab */}
          <TabPanel value={tabValue} index={3}>
            <TeamInviteSupervisors />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Group Form Dialog */}
      <GroupForm
        open={groupFormOpen}
        onClose={() => setGroupFormOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        mode={groupFormMode}
        groupTypes={groupTypes}
        users={users}
        currentUser={currentUser}
      />

      {/* Group Type Form Dialog */}
      <GroupTypeForm
        open={groupTypeFormOpen}
        onClose={() => setGroupTypeFormOpen(false)}
        groupType={editingGroupType}
        onSave={handleSaveGroupType}
        mode={groupTypeFormMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteItem?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Groups; 